import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Field } from '~/components/shared/Field';
import moment from 'moment';
import { useMutation, useQuery } from '@apollo/client';
import { GET_USER, UPDATE_USER_PROFILE } from '~/services/user';
import _pick from 'lodash/pick';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useParams, useSearchParams } from 'react-router-dom';

export const Profile = () => {
  const { personId } = useParams();
  const { data } = useQuery(GET_USER, {
    variables: {
      data: {
        id: personId,
      },
    },
  });
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const user = data?.get_user.data || {};
  const [t] = useTranslation('locale');
  const [showToast, setShowToast] = useState(false);
  const { register, setValue, handleSubmit } = useForm({
    defaultValues: {
      profile_name: 'user?.profile_name || ',
      email: user?.email || '',
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      created_at: user?.created_at || '',
      updated_at: user?.updated_at || '',
      number_id: user?.whatsapp_number?.length
        ? user?.whatsapp_number
        : t('profile.number_not_available'),
      comments: user?.comments || '',
    },
  });
  const [saveProfile, { loading }] = useMutation(UPDATE_USER_PROFILE);

  const onSubmit = async (data: any) => {
    await saveProfile({
      variables: {
        data: {
          ..._pick(data, [
            'profile_name',
            'email',
            'first_name',
            'last_name',
            'comments',
          ]),
          id: user?._id,
        },
      },
    });

    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  useEffect(() => {
    setValue('profile_name', user?.profile_name || '');
    setValue('email', user?.email || '');
    setValue('first_name', user?.first_name || '');
    setValue('last_name', user?.last_name || '');
    setValue(
      'created_at',
      moment(user?.created_at).format('MMM DD YYYY, h:mm a') || '',
    );
    setValue(
      'updated_at',
      moment(user?.updated_at).format('MMM DD YYYY, h:mm a') || '',
    );
    setValue('number_id', user?.whatsapp_number || '');
    setValue('comments', user?.comments || '');
  }, [user]);

  const fields = [
    {
      label: t('profile.profile_name'),
      name: 'profile_name',
      type: 'text',
      editable: true,
    },
    {
      label: t('profile.email'),
      name: 'email',
      type: 'email',
      editable: true,
    },
    {
      label: t('profile.first_name'),
      name: 'first_name',
      type: 'text',
      editable: true,
    },
    {
      label: t('profile.last_name'),
      name: 'last_name',
      type: 'text',
      editable: true,
    },
    {
      label: t('profile.created_at'),
      name: 'created_at',
      type: 'readonly',
      editable: false,
    },
    {
      label: t('profile.updated_at'),
      name: 'updated_at',
      type: 'readonly',
      editable: false,
    },
    {
      label: t('profile.number_id'),
      name: 'number_id',
      type: 'readonly',
      editable: false,
    },
    {
      label: t('profile.comments'),
      name: 'comments',
      type: 'textarea',
      editable: true,
    },
  ];

  useEffect(() => {
    setOpen(Boolean(searchParams.get('profile')));
  }, [searchParams.get('profile')]);

  return (
    <div
      className={`bg-custom-surface-200 h-screen shrink-0 transition-all lg:relative ${
        open ? 'w-full px-3 lg:w-[350px]' : 'w-0'
      }`}
    >
      {open && (
        <>
          <div className="my-6 flex justify-center gap-6">
            <XMarkIcon
              className="w-4 cursor-pointer"
              onClick={() => setSearchParams({ profile: '' })}
            />
            <p className="text-sm font-bold">Información del usuario</p>
          </div>
          <div className="flex h-[60vh] flex-col gap-2 overflow-y-scroll">
            {user?._id ? (
              <div>
                {fields.map((field) => (
                  <Field
                    key={field.name}
                    register={register}
                    fieldName={field.name}
                    label={field.label}
                    type={field.type}
                    validate={() => true}
                  />
                ))}
              </div>
            ) : (
              <>
                <div className="flex h-full items-center justify-center">
                  <p className="w-[300px] text-center text-lg text-neutral-400">
                    {t('profile.no_user')}
                  </p>
                </div>
              </>
            )}
          </div>
          {user?._id && (
            <div className="py-4">
              <button
                className="btn btn-primary rounded-full"
                onClick={handleSubmit(onSubmit)}
              >
                {loading ? (
                  <progress className="progress w-56"></progress>
                ) : (
                  <span>{t('profile.save_profile')}</span>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {showToast && (
        <div className="toast toast-start">
          <div className="alert alert-success">
            <span>{t('profile.info_saved')}</span>
          </div>
        </div>
      )}
    </div>
  );
};
